<script setup lang="ts">
import { ref, watch, computed } from 'vue';

import type {
  AndCriterionInput,
  AndCriterionOutput,
  OrCriterionInput,
  NotCriterionInput,
  EqualsCriterion,
  ContainsCriterion,
  GreaterThanCriterion,
  GreaterEqualThanCriterion,
  LessThanCriterion,
  LessEqualThanCriterion,
  CurrencyAmountCriterion,
  EntityCriterion,
  CalendarEventCriterion,
  ContractualPartyCriterion,
  MatterCriterion,
  OwnerCriterion,
  DateCriterion,
  ListAnyCriterion,
  ListAllCriterion,
  EnumResponseType,
} from '@/api';
import {
  isAndCriterion,
  isOrCriterion,
  isNotCriterion,
  isEqualsCriterion,
  isContainsCriterion,
  isGreaterThanCriterion,
  isGreaterEqualThanCriterion,
  isLessThanCriterion,
  isLessEqualThanCriterion,
  isCurrencyAmountCriterion,
  isEntityCriterion,
  isCalendarEventCriterion,
  isStaticColumnCriterion,
  isListAnyCriterion,
  isListAllCriterion,
  isListCriterion,
  getStaticColumnCriterionFieldAndValue,
  getStaticColumnFields,
  getStaticColumnTypeFromCriterion,
  normalizeCriterion,
  getSpecialCriterionFields,
  type StaticCriterionColumnType,
} from '@/utils/contractManagementView/staticFilters';
import {
  CRITERION_TYPES,
  getCriterionType,
  isStaticColumnType,
  isListOperatorType,
  isComparisonOperatorType,
  isSpecialCriterionType,
  OPERATION_TO_TYPE_MAP,
  type CriterionTypeValue,
  type CriterionItem,
} from '@/utils/contractManagementView/criterionConstants';
import QueryGroup from './QueryGroup.vue';
import GaiaCodeBox from '@/components/design/base/GaiaCodeBox.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';

import {
  getElementType,
  getAvailableFilterOperations,
  type ResponseType,
} from '@/utils/contractManagementView/filterValueValidation';
import { isDatetimeString, formatDatetimeForDisplay } from '@/utils/datetimeUtils';

// The component keeps an internal-only `type` field alongside the API's
// `criterion_type` for UI bookkeeping (e.g. toggling AND/OR), so criterion
// values flowing through this file carry one extra optional field beyond
// what the generated API types declare.
type CriterionInternal = CriterionItem & { type?: CriterionTypeValue };
type GroupInternal = (AndCriterionInput | OrCriterionInput) & { type?: CriterionTypeValue };
// The six comparison/equality leaf criterion types, as used throughout for
// rules whose value can be directly read/written.
type ValueRule = (
  | EqualsCriterion
  | ContainsCriterion
  | GreaterThanCriterion
  | GreaterEqualThanCriterion
  | LessThanCriterion
  | LessEqualThanCriterion
) & { type?: CriterionTypeValue };

interface Props {
  modelValue: AndCriterionInput | AndCriterionOutput | null | undefined;
  availableOptions?: { label: string; value: string | number }[];
  placeholder?: string;
  responseType?: ResponseType;
  enumResponseType?: EnumResponseType;
  isStaticColumn?: boolean;
  staticColumnType?: StaticCriterionColumnType;
  /** Offer "No date" on Equals date rules (`== null`: the row has no such date). */
  allowEmptyDate?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: AndCriterionInput | null): void;
  (e: 'validation-error', hasErrors: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  availableOptions: () => [],
  placeholder: 'Select value...',
  isStaticColumn: true,
  responseType: undefined,
  enumResponseType: undefined,
  staticColumnType: undefined,
  allowEmptyDate: false,
});

const emit = defineEmits<Emits>();

// Track validation errors
const hasValidationErrors = ref(false);

// Handle validation errors from child components
function handleValidationError(_path: number[], hasError: boolean): void {
  // Aggregate validation errors from all rules
  // For simplicity, we'll track if any error exists
  // In a more complex scenario, we could track per-path errors
  hasValidationErrors.value = hasError;
  emit('validation-error', hasValidationErrors.value);
}

// Root filter state
// Internally can be AndCriterionInput or OrCriterionInput for UX flexibility
// When emitting, we ensure it's always AndCriterionInput (backend requirement)
const rootFilter = ref<AndCriterionInput | OrCriterionInput | null>(null);
const isInternalUpdate = ref(false);

// Initialize from modelValue
function initializeFromModelValue(): void {
  if (isInternalUpdate.value) return;

  if (!props.modelValue) {
    rootFilter.value = {
      type: CRITERION_TYPES.AND,
      criterion_type: CRITERION_TYPES.AND,
      criteria: [],
    } as AndCriterionInput;
    return;
  }

  const normalized = normalizeCriterion(props.modelValue);

  // Unwrap if backend sent AndCriterion wrapping a single OrCriterion
  // This improves UX by showing OR at root level when appropriate
  if (
    isAndCriterion(normalized) &&
    normalized.criteria.length === 1 &&
    isOrCriterion(normalized.criteria[0])
  ) {
    // Unwrap the OrCriterion to show it at root level in UI
    rootFilter.value = normalized.criteria[0];
  } else {
    rootFilter.value = normalized as AndCriterionInput | OrCriterionInput;
  }
}

// Watch for external changes
watch(
  () => props.modelValue,
  () => {
    if (!isInternalUpdate.value) {
      initializeFromModelValue();
    }
  },
  { immediate: true, deep: true }
);

// Clean up filter structure before emitting - ensure proper structure for API
// Static column criteria should only have 'criterion_type', not 'type'
// Clean up filter structure for API - convert 'type' to 'criterion_type' and remove 'type'
// The API expects 'criterion_type' field, but we use 'type' internally for consistency

function cleanupFilterForApi(
  criterion: CriterionInternal | null | undefined
): CriterionInternal | null | undefined {
  if (!criterion) return criterion;

  // Clone to avoid mutation

  const cleaned: Record<string, unknown> = { ...criterion };
  const criterionType = getCriterionType(cleaned);

  // 1. Unified type conversion: move 'type' to 'criterion_type' and remove 'type'
  if ('type' in cleaned && !isStaticColumnType(cleaned.type)) {
    cleaned.criterion_type = cleaned.type;
    delete cleaned.type;
  } else if ('type' in cleaned) {
    // Static columns keep criterion_type, remove type if somehow it exists
    delete cleaned.type;
  }

  // 2. Recursive cleaning for nested structures (AND, OR, NOT, LIST_ANY, LIST_ALL)
  if ('criteria' in cleaned && Array.isArray(cleaned.criteria)) {
    cleaned.criteria = cleaned.criteria.map((c: CriterionInternal) => cleanupFilterForApi(c));
  }

  if ('criterion' in cleaned && cleaned.criterion) {
    cleaned.criterion = cleanupFilterForApi(cleaned.criterion);
  }

  // 3. Recursive cleaning for Complex Fields (CurrencyAmount, Entity, CalendarEvent)
  if (criterionType === CRITERION_TYPES.CURRENCY_AMOUNT) {
    if (cleaned.amount) cleaned.amount = cleanupFilterForApi(cleaned.amount);
    if (cleaned.iso_4217_alpha_3)
      cleaned.iso_4217_alpha_3 = cleanupFilterForApi(cleaned.iso_4217_alpha_3);
  } else if (criterionType === CRITERION_TYPES.ENTITY) {
    if (cleaned.name) cleaned.name = cleanupFilterForApi(cleaned.name);
    if (cleaned.email) cleaned.email = cleanupFilterForApi(cleaned.email);
  } else if (criterionType === CRITERION_TYPES.CALENDAR_EVENT && cleaned.event_date)
    cleaned.event_date = cleanupFilterForApi(cleaned.event_date);

  // 4. Special handling for comparison operators (ensure numeric values for number types)
  if (
    isComparisonOperatorType(criterionType) &&
    'value' in cleaned && // An unset value stays '' (and fails validation): null is a real filter
    // value — `== null` means "is empty" (e.g. "No date") — so it is only
    // sent when the rule asks for it.
    typeof cleaned.value === 'string' &&
    cleaned.value.trim() !== ''
  ) {
    const numValue = Number(cleaned.value);
    if (!isNaN(numValue)) cleaned.value = numValue;
  }

  // 5. Recursive cleaning for static column criterion fields
  if (isStaticColumnType(criterionType)) {
    const fields = getStaticColumnFields(criterionType);
    for (const field of fields) {
      if (field.key in cleaned && cleaned[field.key]) {
        cleaned[field.key] = cleanupFilterForApi(cleaned[field.key] as CriterionInternal);
      }
    }
  }

  return cleaned;
}

// Emit changes
function emitUpdate(): void {
  isInternalUpdate.value = true;

  // Ensure root filter is always AndCriterion (backend requirement)
  // If root is OrCriterion, wrap it in AndCriterion
  let filterToEmit: AndCriterionInput | null = null;

  if (!rootFilter.value) {
    filterToEmit = null;
  } else if (isOrCriterion(rootFilter.value)) {
    // Wrap OrCriterion in AndCriterion to satisfy backend requirement
    // The backend API requires top-level to be AndCriterion, but we allow
    // users to use OR at the root level in the UI for better UX
    filterToEmit = {
      criterion_type: CRITERION_TYPES.AND,
      criteria: [rootFilter.value],
    };
  } else if (isAndCriterion(rootFilter.value)) {
    filterToEmit = rootFilter.value;
  } else {
    // Fallback: wrap any other type in AndCriterion
    filterToEmit = {
      criterion_type: CRITERION_TYPES.AND,
      criteria: [rootFilter.value],
    };
  }

  // Clean up the filter before emitting - remove 'type' from static column criteria
  if (filterToEmit) {
    filterToEmit = cleanupFilterForApi(filterToEmit) as AndCriterionInput;
    // Debug: Log the cleaned filter structure
    // console.log('Cleaned filter to emit:', JSON.stringify(filterToEmit, null, 2));
  }

  emit('update:modelValue', filterToEmit);
  setTimeout(() => {
    isInternalUpdate.value = false;
  }, 0);
}

// Drag and Drop State
interface DragState {
  sourcePath: number[];
  sourceCriterion: CriterionItem;
  type: 'rule' | 'group';
}

const dragState = ref<DragState | null>(null);
const dragOverGroupPath = ref<number[] | null>(null);

// Helper: Convert path array to string for comparison
function pathToString(path: number[]): string {
  return path.join('-');
}

// Check if a criterion is a simple value-based rule (can have its value updated)
function isValueBasedRule(criterion: unknown): boolean {
  return (
    isEqualsCriterion(criterion) ||
    isContainsCriterion(criterion) ||
    isGreaterThanCriterion(criterion) ||
    isGreaterEqualThanCriterion(criterion) ||
    isLessThanCriterion(criterion) ||
    isLessEqualThanCriterion(criterion)
  );
}

// Extract value from a value-based rule
function extractValueFromRule(rule: ValueRule): string | number {
  const value = rule.value;
  if (value === null) return '';
  if (typeof value === 'boolean') return String(value);
  return value;
}

// Update value for CurrencyAmount criterion
function updateCurrencyAmountValue(
  criterion: CurrencyAmountCriterion,
  value: string | number
): boolean {
  if (!criterion.amount) return false;
  const amountCrit = criterion.amount as ValueRule;
  if ('value' in amountCrit) {
    amountCrit.value = value;
    return true;
  }
  return false;
}

// Update value for Entity criterion
function updateEntityValue(criterion: EntityCriterion, value: string | number): boolean {
  if (!criterion.name) return false;
  const nameCrit = criterion.name;
  if ('value' in nameCrit) {
    nameCrit.value = value;
    return true;
  }
  return false;
}

// Get criterion at path
function getCriterionAtPath(path: number[]): CriterionItem | null {
  let current: CriterionItem | null = rootFilter.value;
  for (const index of path) {
    if (!current || !('criteria' in current)) return null;
    current = current.criteria[index];
  }
  return current;
}

// Get parent criterion at path
function getParentCriterionAtPath(
  path: number[]
): AndCriterionInput | OrCriterionInput | NotCriterionInput | null {
  if (path.length === 0) return rootFilter.value;
  const parentPath = path.slice(0, -1);
  const parent = getCriterionAtPath(parentPath);
  // Parent can be AND, OR, or NOT (for NOT groups wrapping complex logic)
  return parent as AndCriterionInput | OrCriterionInput | NotCriterionInput | null;
}

// Check if target path is a descendant of source path
function isDescendantPath(sourcePath: number[], targetPath: number[]): boolean {
  if (targetPath.length <= sourcePath.length) return false;
  return sourcePath.every((val, idx) => val === targetPath[idx]);
}

// Check if a NOT criterion wraps a simple rule (not a group)
function isNotWrappedSimpleRule(criterion: CriterionItem): boolean {
  if (!isNotCriterion(criterion)) return false;
  const inner = (criterion as NotCriterionInput).criterion;
  return isEqualsCriterion(inner) || isContainsCriterion(inner);
}

// Handle drag start
function handleDragStart(event: DragEvent, criterion: CriterionItem, path: number[]): void {
  const isGroup =
    isAndCriterion(criterion) ||
    isOrCriterion(criterion) ||
    (isNotCriterion(criterion) && !isNotWrappedSimpleRule(criterion));

  // Deep copy the criterion to avoid reference issues
  dragState.value = {
    sourcePath: [...path],
    sourceCriterion: JSON.parse(JSON.stringify(criterion)) as CriterionItem,
    type: isGroup ? 'group' : 'rule',
  };

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', pathToString(path));
  }

  // Add dragging class for visual feedback
  const target = event.currentTarget as HTMLElement | null;
  if (target) {
    target.classList.add('dragging');
  }
}

// Handle drag end
function handleDragEnd(event?: DragEvent): void {
  // Remove dragging class
  const target = event?.currentTarget as HTMLElement | null | undefined;
  if (target) {
    target.classList.remove('dragging');
  }

  dragState.value = null;
  dragOverGroupPath.value = null;
}

// Handle drag over
function handleDragOver(event: DragEvent, targetGroupPath: number[]): void {
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return;

  if (!dragState.value) {
    dataTransfer.dropEffect = 'none';
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const { sourcePath } = dragState.value;

  // Don't allow dropping on itself
  if (pathToString(sourcePath) === pathToString(targetGroupPath)) {
    dataTransfer.dropEffect = 'none';
    dragOverGroupPath.value = null;
    return;
  }

  // Don't allow dropping a group into its own descendant
  if (isDescendantPath(sourcePath, targetGroupPath)) {
    dataTransfer.dropEffect = 'none';
    dragOverGroupPath.value = null;
    return;
  }

  // Valid drop target
  dataTransfer.dropEffect = 'move';
  dragOverGroupPath.value = targetGroupPath;
}

// Handle drop
function handleDrop(event: DragEvent, targetGroupPath: number[]): void {
  event.preventDefault();
  event.stopPropagation();

  if (!dragState.value) {
    handleDragEnd(event);
    return;
  }

  const { sourcePath, sourceCriterion } = dragState.value;

  // Validate drop
  if (pathToString(sourcePath) === pathToString(targetGroupPath)) {
    handleDragEnd(event);
    return;
  }

  if (isDescendantPath(sourcePath, targetGroupPath)) {
    handleDragEnd(event);
    return;
  }

  // Get target group. Drop zones only exist on AND/OR group containers (see
  // QueryGroup.vue), so this is always an AND/OR group in practice — NOT and
  // LIST_ANY/LIST_ALL wrappers are never drop targets.
  const targetGroup = getCriterionAtPath(targetGroupPath);
  if (!targetGroup || !('criteria' in targetGroup)) {
    handleDragEnd(event);
    return;
  }

  // Get source parent to remove the item. Draggable items are only ever
  // addressed as direct children of an AND/OR group's `criteria` array (see
  // QueryGroup.vue), so this is always an AND/OR group in practice too.
  const sourceParent = getParentCriterionAtPath(sourcePath);
  if (!sourceParent || !('criteria' in sourceParent)) {
    handleDragEnd(event);
    return;
  }

  // Remove from source
  const sourceIndex = sourcePath.at(-1);
  if (sourceIndex !== undefined) {
    sourceParent.criteria.splice(sourceIndex, 1);
  }

  // Add to target at the bottom
  targetGroup.criteria.push(sourceCriterion);

  emitUpdate();
  handleDragEnd(event);
}

// Add rule to group with specified type
// Note: NOT wrapping is handled via toggle switch, not during rule creation
function addRule(path: number[], ruleType: CriterionTypeValue = CRITERION_TYPES.CONTAINS): void {
  const parent = getCriterionAtPath(path);
  if (!parent) return;

  // For static columns with 'in' (contain) rule, create a static column criterion
  // For '==' (equal) rule or non-static columns, create regular EqualsCriterion/ContainsCriterion
  let rule:
    | EqualsCriterion
    | ContainsCriterion
    | GreaterThanCriterion
    | GreaterEqualThanCriterion
    | LessThanCriterion
    | LessEqualThanCriterion
    | CurrencyAmountCriterion
    | EntityCriterion
    | CalendarEventCriterion
    | ContractualPartyCriterion
    | MatterCriterion
    | OwnerCriterion
    | DateCriterion
    | ListAnyCriterion
    | ListAllCriterion;

  if (props.isStaticColumn && props.staticColumnType && ruleType === CRITERION_TYPES.CONTAINS) {
    // Create a static column criterion with first field pre-selected

    const staticCriterion: Record<string, unknown> = {
      criterion_type: props.staticColumnType,
    };

    // Get available fields and select the first one by default
    const fields = getStaticColumnFields(props.staticColumnType);
    if (fields.length > 0) {
      const firstField = fields[0];
      // Create an empty ContainsCriterion for the first field — or an Equals one
      // for a date / boolean field, which can't be matched by substring

      const operator = firstField.kind ? CRITERION_TYPES.EQUALS : CRITERION_TYPES.CONTAINS;
      const fieldCriterion: ContainsCriterion & { type?: CriterionTypeValue } = {
        type: operator,
        criterion_type: operator,
        value: '',
      } as ContainsCriterion & { type?: CriterionTypeValue };
      staticCriterion[firstField.key] = fieldCriterion;
    }

    rule = staticCriterion;
  } else if (ruleType === CRITERION_TYPES.LIST_ANY || ruleType === CRITERION_TYPES.LIST_ALL) {
    // Create a list criterion with a sensible inner criterion
    const elementType = props.responseType ? getElementType(props.responseType) : null;

    // For multiple choice enums, the inner operation should always be 'equals'
    // For other list types, determine based on element type
    let innerType: CriterionTypeValue;
    if (props.enumResponseType?.multiple_choices === true) {
      // Multiple choice enum: inner operation is always equals
      innerType = CRITERION_TYPES.EQUALS;
    } else {
      const availableInnerOps = elementType
        ? getAvailableFilterOperations(elementType, undefined)
        : ['equals'];
      const firstOp = availableInnerOps[0];
      innerType = OPERATION_TO_TYPE_MAP[firstOp];
    }

    const innerCriterion: Record<string, unknown> = {
      criterion_type: innerType,
    };

    // Initialize values based on type
    switch (innerType) {
      case CRITERION_TYPES.CURRENCY_AMOUNT: {
        innerCriterion.amount = { criterion_type: CRITERION_TYPES.EQUALS, value: '' };

        break;
      }
      case CRITERION_TYPES.ENTITY: {
        innerCriterion.name = { criterion_type: CRITERION_TYPES.CONTAINS, value: '' };

        break;
      }
      case CRITERION_TYPES.CALENDAR_EVENT: {
        innerCriterion.event_date = { criterion_type: CRITERION_TYPES.EQUALS, value: '' };

        break;
      }
      default: {
        innerCriterion.value = '';
      }
    }

    const listCriterion: Record<string, unknown> = {
      criterion_type: ruleType,
      criterion: innerCriterion,
    };

    rule = listCriterion;
  } else {
    // Create criterion based on ruleType

    const newRule: Record<string, unknown> = {
      type: ruleType,
      criterion_type: ruleType,
      value:
        ruleType === CRITERION_TYPES.CURRENCY_AMOUNT ||
        ruleType === CRITERION_TYPES.ENTITY ||
        ruleType === CRITERION_TYPES.CALENDAR_EVENT
          ? null
          : '',
    };

    // Handle special criterion types - initialize with the first available field for better UX
    switch (ruleType) {
      case CRITERION_TYPES.CURRENCY_AMOUNT: {
        newRule.amount = {
          criterion_type: CRITERION_TYPES.EQUALS,
          value: '',
        };

        break;
      }
      case CRITERION_TYPES.ENTITY: {
        newRule.name = {
          criterion_type: CRITERION_TYPES.CONTAINS,
          value: '',
        };

        break;
      }
      case CRITERION_TYPES.CALENDAR_EVENT: {
        newRule.event_date = {
          criterion_type: CRITERION_TYPES.EQUALS,
          value: '',
        };

        break;
      }
      // No default
    }

    rule = newRule;
  }

  // Add rule to AND/OR groups (NOT is handled via toggle switch, not during rule creation)
  if ('criteria' in parent) {
    // Always add rule directly - NOT wrapping is done via toggle switch
    parent.criteria.push(rule);
  }
  emitUpdate();
}

// Add group to group. `path` addresses an existing AND/OR group (QueryGroup.vue
// only ever renders the add-group menu on AND/OR containers, never on the
// NOT/LIST_ANY/LIST_ALL wrappers, which can only ever hold a single leaf value).
function addGroup(path: number[], type: CRITERION_TYPES.AND | CRITERION_TYPES.OR): void {
  const parent = getCriterionAtPath(path);
  if (!parent || !('criteria' in parent)) return;

  const newGroup: GroupInternal = {
    type,
    criterion_type: type,
    criteria: [],
  };
  parent.criteria.push(newGroup);
  emitUpdate();
}

// Remove item
function removeItem(path: number[]): void {
  if (path.length === 0) {
    rootFilter.value = {
      criterion_type: CRITERION_TYPES.AND,
      criteria: [],
    };
    emitUpdate();
    return;
  }

  // Removable items are always addressed as direct children of an AND/OR
  // group's `criteria` array (see QueryGroup.vue) — NOT-wrapped rules and
  // LIST_ANY/LIST_ALL criteria are removed as a whole from that array too.
  const parent = getCriterionAtPath(path.slice(0, -1));
  if (!parent || !('criteria' in parent)) return;

  const index = path.at(-1);
  if (index !== undefined) {
    parent.criteria.splice(index, 1);
    emitUpdate();
  }
}

// Toggle group operator
function toggleGroupOperator(path: number[]): void {
  const group = getCriterionAtPath(path) as GroupInternal | null;
  if (!group || (!isAndCriterion(group) && !isOrCriterion(group))) return;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const currentType = group.type || group.criterion_type;
  const newType = currentType === CRITERION_TYPES.AND ? CRITERION_TYPES.OR : CRITERION_TYPES.AND;
  group.type = newType;
  group.criterion_type = newType;
  emitUpdate();
}

// Toggle NOT on/off for a rule
function toggleRuleNot(path: number[]): void {
  const parent = getParentCriterionAtPath(path);
  if (!parent || !('criteria' in parent)) return;

  const index = path.at(-1);
  if (index === undefined) return;
  const item = parent.criteria[index];

  if (isNotCriterion(item)) {
    // Remove NOT wrapper - extract inner rule
    const innerRule = item.criterion;
    // Normalize to ensure proper structure (especially criterion_type for static columns)

    const normalizedRule = normalizeCriterion(innerRule) as CriterionInternal;
    parent.criteria[index] = normalizedRule;
  } else {
    // Wrap any rule type in NOT

    const notWrapped: NotCriterionInput & { type?: CriterionTypeValue } = {
      type: CRITERION_TYPES.NOT,
      criterion_type: CRITERION_TYPES.NOT,
      criterion: item,
    };
    parent.criteria[index] = notWrapped;
  }

  emitUpdate();
}

// Update rule value
function updateRuleValue(
  path: number[],
  value: string | number | (string | number)[] | null
): void {
  // Get the criterion at path - could be a rule, NOT-wrapped rule, or NOT group
  const criterion = getCriterionAtPath(path);
  if (!criterion) return;

  let rule: ValueRule | null = null;

  // If it's a NOT group (wrapper for complex logic), get the inner criterion
  if (isNotCriterion(criterion)) {
    const inner = criterion.criterion;
    if (isValueBasedRule(inner)) {
      rule = inner as ValueRule;
    } else {
      // Inner is a group, can't update value
      return;
    }
  } else if (isListAnyCriterion(criterion) || isListAllCriterion(criterion)) {
    // List criteria wrap an inner criterion - update the inner criterion's value
    const listCrit = criterion as ListAnyCriterion | ListAllCriterion;
    const inner = listCrit.criterion;

    if (isCurrencyAmountCriterion(inner)) {
      if (updateCurrencyAmountValue(inner, value as string | number)) {
        emitUpdate();
        return;
      }
    } else if (isEntityCriterion(inner)) {
      if (updateEntityValue(inner, value as string | number)) {
        emitUpdate();
        return;
      }
    } else if (isValueBasedRule(inner)) {
      rule = inner as ValueRule;
    } else {
      // Inner is not a value-based rule, can't update
      return;
    }
  } else if (isCurrencyAmountCriterion(criterion)) {
    // For CurrencyAmount, update the 'amount' field's value
    if (updateCurrencyAmountValue(criterion, value as string | number)) {
      emitUpdate();
    }
    return;
  } else if (isEntityCriterion(criterion)) {
    // For Entity, update the 'name' field's value
    if (updateEntityValue(criterion, value as string | number)) {
      emitUpdate();
    }
    return;
  } else if (isValueBasedRule(criterion)) {
    rule = criterion as ValueRule;
  }

  if (!rule) return;

  // Preserve type/criterion_type when updating value

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  const currentType = rule.type || rule.criterion_type;

  if (isContainsCriterion(rule)) {
    // For non-static columns, Contains is substring matching (single string)
    // For static columns, preserve comma-separated format if needed
    if (props.isStaticColumn) {
      // Static column: preserve comma-separated format
      rule.value = Array.isArray(value) ? value.map(v => String(v)).join(',') : String(value);
    } else {
      // Non-static column: single string for substring matching
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
      rule.value = Array.isArray(value) ? String(value[0] || '') : String(value || '');
    }
    // Ensure both fields are set (or at least criterion_type is present)
    rule.criterion_type ??= CRITERION_TYPES.CONTAINS;
  } else if (
    isEqualsCriterion(rule) ||
    isGreaterThanCriterion(rule) ||
    isGreaterEqualThanCriterion(rule) ||
    isLessThanCriterion(rule) ||
    isLessEqualThanCriterion(rule)
  ) {
    // For comparison operators and equals, store the value as provided (usually a string from NInput)
    // Conversion to number for numeric types is handled in cleanupFilterForApi before emitting to the API
    // This prevents issues with eager conversion while typing (e.g. losing decimal points or trailing spaces)
    const singleValue = Array.isArray(value) ? value.at(0) : value;

    // null is a deliberate "is empty" (QueryGroup only sends it for "No date");
    // a cleared input arrives as ''.
    if (singleValue === undefined) {
      rule.value = '';
    } else {
      // Keep as string or number as provided
      rule.value = singleValue;
    }

    // Ensure criterion_type is preserved
    // Use existing type if available, otherwise determine from rule
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    const expectedType = currentType || getCriterionType(rule);

    if (expectedType) {
      // `rule`'s narrowed criterion_type is a small literal subset, but
      // `expectedType` is any CriterionTypeValue determined at runtime by
      // getCriterionType — write through an untyped view rather than
      // asserting a specific (and possibly wrong) literal.
      const ruleFields = rule as Record<string, unknown>;
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- untyped view: an empty-string value counts as unset here
      if (!ruleFields.criterion_type) ruleFields.criterion_type = expectedType;
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- untyped view: an empty-string value counts as unset here
      if (!ruleFields.type) ruleFields.type = expectedType;
    }
  }

  emitUpdate();
}

// Update a specific field + operator + value in an object-based criterion (Static column, Currency, Entity, etc.)
function updateObjectField(
  path: number[],
  field: string,
  operator: CriterionTypeValue,
  value: string | number
): void {
  const criterion = getCriterionAtPath(path);
  if (!criterion) return;

  let target: Record<string, unknown> | null = null;

  // Handle list criteria with special type inner criteria
  if (isListAnyCriterion(criterion) || isListAllCriterion(criterion)) {
    const inner = criterion.criterion;
    if (
      isCurrencyAmountCriterion(inner) ||
      isEntityCriterion(inner) ||
      isCalendarEventCriterion(inner)
    ) {
      // Update the inner criterion's field
      target = inner;
    } else {
      // Not a special type inner criterion, use normal handling
      target = criterion;
    }
  } else if (isNotCriterion(criterion)) {
    target = criterion.criterion;
  } else {
    target = criterion;
  }

  if (typeof target !== 'object') return;

  // For complex types, clear other fields to ensure only one is active
  const type = getCriterionType(target);
  const fields = type
    ? isStaticColumnType(type)
      ? getStaticColumnFields(type)
      : isSpecialCriterionType(type)
        ? getSpecialCriterionFields(type)
        : []
    : [];
  for (const f of fields) {
    if (f.key !== field) {
      delete target[f.key];
    }
  }

  // Create the field criterion with the specified operator and value
  const isNumericOp = isComparisonOperatorType(operator) && operator !== CRITERION_TYPES.CONTAINS;
  const isBooleanField = fields.find(f => f.key === field)?.kind === 'boolean';
  const normalizedValue =
    isBooleanField && (value === 'true' || value === 'false')
      ? value === 'true'
      : isNumericOp && !isNaN(Number(value)) && value !== ''
        ? Number(value)
        : String(value);

  target[field] = {
    criterion_type: operator,
    value: normalizedValue,
  };

  emitUpdate();
}

// Create a list criterion with inner criterion
function createListCriterion(
  listType: CRITERION_TYPES.LIST_ANY | CRITERION_TYPES.LIST_ALL,
  innerCriterion: CriterionItem
): ListAnyCriterion | ListAllCriterion {
  const inner = JSON.parse(JSON.stringify(innerCriterion));
  // Ensure it has criterion_type
  if (!inner.criterion_type && inner.type) {
    inner.criterion_type = inner.type;
    delete inner.type;
  }

  if (listType === CRITERION_TYPES.LIST_ANY) {
    return {
      criterion_type: CRITERION_TYPES.LIST_ANY,
      criterion: inner,
    };
  } else {
    return {
      criterion_type: CRITERION_TYPES.LIST_ALL,
      criterion: inner,
    };
  }
}

// Initialize special criterion type fields
function initializeSpecialCriterionFields(
  rule: Record<string, unknown>,
  type: CriterionTypeValue
): void {
  if (type === CRITERION_TYPES.CURRENCY_AMOUNT) {
    rule.amount = {
      criterion_type: CRITERION_TYPES.GREATER_THAN,
      value: '',
    };
    rule.iso_4217_alpha_3 = {
      criterion_type: CRITERION_TYPES.EQUALS,
      value: '',
    };
    delete rule.value;
  } else if (type === CRITERION_TYPES.ENTITY) {
    rule.name = {
      criterion_type: CRITERION_TYPES.CONTAINS,
      value: '',
    };
    delete rule.value;
  }
}

// Change rule type (e.g., from Equals to Greater Than)
function changeRuleType(path: number[], newType: CriterionTypeValue): void {
  const criterion = getCriterionAtPath(path);
  if (!criterion) return;

  let currentRule: CriterionItem | null = null;

  let parent: AndCriterionInput | OrCriterionInput | NotCriterionInput | null = null;

  // Get parent and current rule
  // For NOT-wrapped rules, we need to update the inner rule while keeping the NOT wrapper
  let isNotWrapped = false;

  if (isNotCriterion(criterion)) {
    isNotWrapped = true;
    parent = getParentCriterionAtPath(path);
    const innerRule = criterion.criterion;
    currentRule = innerRule; // Work with the inner rule for type switching
  } else {
    currentRule = criterion;
    // Find parent to update
    const parentPath = path.slice(0, -1);
    parent = getCriterionAtPath(parentPath) as
      AndCriterionInput | OrCriterionInput | NotCriterionInput | null;
  }

  // 1. Handle switching the INNER rule type of a list criterion
  if (isListCriterion(currentRule) && isComparisonOperatorType(newType)) {
    const listCrit = currentRule;
    const inner = listCrit.criterion;

    const innerValue = isValueBasedRule(inner) ? extractValueFromRule(inner as ValueRule) : '';

    const newInner: ValueRule = {
      criterion_type: newType,
      value: innerValue,
    } as ValueRule;

    listCrit.criterion = newInner;
    emitUpdate();
    return;
  }

  // 2. Create the new rule

  let newRule: CriterionInternal | null = null;

  if (isListOperatorType(newType)) {
    // Switching TO a list operator - wrap existing rule if compatible
    // OR switching BETWEEN list operators (list_any <-> list_all)

    // Check if we're switching between list operators (preserve inner criterion)
    if (isListCriterion(currentRule)) {
      const listCrit = currentRule;
      newRule = createListCriterion(newType, listCrit.criterion);
    } else if (
      isComparisonOperatorType(getCriterionType(currentRule)) ||
      isCurrencyAmountCriterion(currentRule) ||
      isEntityCriterion(currentRule) ||
      isCalendarEventCriterion(currentRule)
    ) {
      // Switching from a comparison operator or special type TO a list operator
      newRule = createListCriterion(newType, currentRule);
    } else {
      // Default: create simple equals criterion
      const inner = {
        criterion_type: CRITERION_TYPES.EQUALS,
        value: '',
      };
      newRule = createListCriterion(newType, inner as CriterionItem);
    }
  } else if (isListCriterion(currentRule) && !isListOperatorType(newType)) {
    // Switching FROM a list operator - unwrap inner rule if compatible, or use its value
    const listCrit = currentRule;
    const inner = listCrit.criterion;
    const currentValue = isValueBasedRule(inner) ? extractValueFromRule(inner as ValueRule) : '';

    newRule = {
      criterion_type: newType,
      value: currentValue,
    } as ValueRule;
  } else if (
    props.isStaticColumn &&
    props.staticColumnType &&
    isStaticColumnCriterion(currentRule)
  ) {
    // Static column criteria
    if (newType === CRITERION_TYPES.EQUALS || newType === CRITERION_TYPES.CONTAINS) {
      const fieldAndValue = getStaticColumnCriterionFieldAndValue(currentRule);
      if (fieldAndValue) {
        // Create static column criterion with dynamic field assignment

        const staticCriterion: Record<string, unknown> = {
          criterion_type: props.staticColumnType,
        };
        staticCriterion[fieldAndValue.field] = {
          criterion_type: newType,
          value: fieldAndValue.value || '',
        };

        newRule = staticCriterion;
      }
    }
  } else {
    // Normal rule switching
    const currentValue = isValueBasedRule(currentRule)
      ? extractValueFromRule(currentRule as ValueRule)
      : '';

    newRule = {
      criterion_type: newType,
      value: currentValue,
    } as CriterionInternal;
  }

  if (!newRule) return;

  // Handle special criterion types
  initializeSpecialCriterionFields(newRule, newType);

  // 3. Update the rule
  // If it was a NOT-wrapped rule, update the inner criterion of the NOT wrapper
  if (isNotWrapped) {
    // Find the NOT wrapper in the parent's criteria array and update its inner criterion
    if (parent && (isAndCriterion(parent) || isOrCriterion(parent))) {
      const index = path.at(-1);
      if (index !== undefined) {
        const notWrapper = parent.criteria[index];
        if (isNotCriterion(notWrapper)) {
          notWrapper.criterion = newRule;
        }
      }
    }
  } else if (parent && (isAndCriterion(parent) || isOrCriterion(parent))) {
    const index = path.at(-1);
    if (index !== undefined) {
      parent.criteria[index] = newRule;
    }
  }
  // `changeRuleType` is only ever invoked for a rule row nested inside an
  // AND/OR group (see QueryGroup.vue), never for the root itself, so there's
  // no case where `parent` is absent here.

  emitUpdate();
}

// Generate readable query text
function generateQueryText(criterion: CriterionItem | null, depth = 0): string {
  if (!criterion) return 'No filters';

  if (isNotCriterion(criterion)) {
    const inner = criterion.criterion;
    const innerText = generateQueryText(inner, depth + 1);

    // Check if inner is a rule or a group
    const isGroup = isAndCriterion(inner) || isOrCriterion(inner);

    if (isGroup) {
      return `not (${innerText})`;
    }

    // For simple rules, we can just say "not equals X" or similar
    return `not ${innerText}`;
  }

  // Handle list criteria (list_any, list_all)
  if (isListAnyCriterion(criterion)) {
    const inner = criterion.criterion as CriterionItem;
    return `any element of the list ${generateQueryText(inner, depth + 1)}`;
  }

  if (isListAllCriterion(criterion)) {
    const inner = criterion.criterion as CriterionItem;
    return `all elements of the list ${generateQueryText(inner, depth + 1)}`;
  }

  if (isAndCriterion(criterion) || isOrCriterion(criterion)) {
    const operator = isAndCriterion(criterion) ? 'AND' : 'OR';
    const subCriteria = criterion.criteria;

    if (subCriteria.length === 0) return 'Empty group';

    const parts = subCriteria
      .map(c => {
        const text = generateQueryText(c, depth + 1);
        if (isAndCriterion(c) || isOrCriterion(c)) {
          return `(${text})`;
        }
        return text;
      })
      .filter(Boolean);

    if (parts.length === 0) return 'Empty group';
    if (parts.length === 1) return parts[0];

    return parts.join(` ${operator} `);
  }

  // Value criteria
  if (isEqualsCriterion(criterion)) {
    const value = criterion.value;
    const option = props.availableOptions.find(opt => opt.value === value);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    let displayValue = option?.label || String(value ?? '');

    // Check if it's a datetime value (and not from availableOptions)
    if (!option && isDatetimeString(displayValue)) {
      displayValue = formatDatetimeForDisplay(displayValue);
      return `date equals ${displayValue}`;
    }

    return `equals ${displayValue}`;
  }

  if (isContainsCriterion(criterion)) {
    const value = criterion.value || '';
    if (!value) return 'contains (empty)';
    return `contains "${value}"`;
  }

  if (isGreaterThanCriterion(criterion)) {
    const value = criterion.value;
    if (isDatetimeString(value)) {
      return `date > ${formatDatetimeForDisplay(String(value))}`;
    }
    return `> ${value}`;
  }
  if (isGreaterEqualThanCriterion(criterion)) {
    const value = criterion.value;
    if (isDatetimeString(value)) {
      return `date >= ${formatDatetimeForDisplay(String(value))}`;
    }
    return `>= ${value}`;
  }
  if (isLessThanCriterion(criterion)) {
    const value = criterion.value;
    if (isDatetimeString(value)) {
      return `date < ${formatDatetimeForDisplay(String(value))}`;
    }
    return `< ${value}`;
  }
  if (isLessEqualThanCriterion(criterion)) {
    const value = criterion.value;
    if (isDatetimeString(value)) {
      return `date <= ${formatDatetimeForDisplay(String(value))}`;
    }
    return `<= ${value}`;
  }

  // Complex criteria
  if (isCurrencyAmountCriterion(criterion)) {
    const parts = [];
    if (criterion.amount) parts.push(`amount ${generateQueryText(criterion.amount)}`);
    if (criterion.iso_4217_alpha_3)
      parts.push(`currency ${generateQueryText(criterion.iso_4217_alpha_3)}`);
    return parts.length > 1 ? `(${parts.join(' AND ')})` : parts[0] || 'currency amount filter';
  }

  if (isEntityCriterion(criterion)) {
    const parts = [];
    if (criterion.name) parts.push(`name ${generateQueryText(criterion.name)}`);
    if (criterion.email) parts.push(`email ${generateQueryText(criterion.email)}`);
    return parts.length > 1
      ? `(entity ${parts.join(' AND ')})`
      : parts[0]
        ? `entity ${parts[0]}`
        : 'entity filter';
  }

  if (isCalendarEventCriterion(criterion)) {
    const parts = [];
    if (criterion.event_date) {
      const eventDateText = generateQueryText(criterion.event_date);
      // Remove "date" prefix if it exists (from datetime formatting) to avoid "date date equals"
      // The inner criterion might return "date equals X" or "date > X", so we remove "date " prefix
      const cleanedText = eventDateText.replace(/^date /, '');
      parts.push(`calendar event date ${cleanedText}`);
    }
    if (criterion.recurrence_type) {
      const recurrenceText = generateQueryText(criterion.recurrence_type);
      parts.push(`recurrence type ${recurrenceText}`);
    }
    if (criterion.recurrence_count) {
      const countText = generateQueryText(criterion.recurrence_count);
      parts.push(`recurrence count ${countText}`);
    }
    if (criterion.recurrence_until_date) {
      const untilDateText = generateQueryText(criterion.recurrence_until_date);
      // Remove "date" prefix if it exists (from datetime formatting)
      const cleanedText = untilDateText.replace(/^date /, '');
      parts.push(`recurrence until date ${cleanedText}`);
    }
    if (criterion.recurrence_frequency) {
      const frequencyText = generateQueryText(criterion.recurrence_frequency);
      parts.push(`recurrence frequency ${frequencyText}`);
    }
    if (criterion.recurrence_interval) {
      const intervalText = generateQueryText(criterion.recurrence_interval);
      parts.push(`recurrence interval ${intervalText}`);
    }
    return parts.length > 1 ? `(${parts.join(' AND ')})` : parts[0] || 'calendar event filter';
  }

  // Handle static column criteria
  if (isStaticColumnCriterion(criterion)) {
    const fieldAndValue = getStaticColumnCriterionFieldAndValue(criterion);
    if (fieldAndValue) {
      const fields = getStaticColumnFields(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        getStaticColumnTypeFromCriterion(criterion) || 'contractual_party'
      );
      const fieldLabel =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        fields.find(f => f.key === fieldAndValue.field)?.label || fieldAndValue.field;
      if (fieldAndValue.ruleType === CRITERION_TYPES.EQUALS) {
        return `${fieldLabel} equals ${fieldAndValue.value}`;
      } else if (fieldAndValue.ruleType === CRITERION_TYPES.CONTAINS) {
        return `${fieldLabel} contains ${fieldAndValue.value}`;
      } else {
        // Date fields compare: "Date > 2026-01-01"
        return `${fieldLabel} ${fieldAndValue.ruleType} ${fieldAndValue.value}`;
      }
    }
    return 'static column filter (empty)';
  }

  return 'Unknown filter';
}

const queryText = computed(() => {
  return generateQueryText(rootFilter.value);
});

/** Date filters compare whole UTC days, so the time part of a value is ignored. */
const isDateColumn = computed(() => {
  const elementType = props.responseType ? getElementType(props.responseType) : null;
  return elementType === 'date' || elementType === 'datetime';
});
</script>

<template>
  <div class="recursive-query-builder">
    <!-- The generated expression, in the design system's readout box (mono is
         what makes the parentheses and operators scannable). -->
    <gaia-code-box label="Query preview" :style="{ marginBottom: '16px' }">
      <gaia-text v-if="queryText === 'No filters'" tone="blank">No filters</gaia-text>
      <template v-else>{{ queryText }}</template>
    </gaia-code-box>
    <gaia-text
      v-if="isDateColumn"
      size="meta"
      tone="muted"
      :style="{ display: 'block', marginTop: '-8px', marginBottom: '16px' }"
    >
      Dates are compared by whole day (UTC): "after" means from the next day on.
    </gaia-text>

    <!-- Query Builder -->
    <QueryGroup
      :criterion="rootFilter"
      :path="[]"
      :depth="0"
      :available-options="availableOptions"
      :placeholder="placeholder"
      :response-type="responseType"
      :enum-response-type="enumResponseType"
      :is-static-column="isStaticColumn"
      :allow-empty-date="allowEmptyDate"
      :static-column-type="staticColumnType"
      :drag-state="dragState"
      :drag-over-group-path="dragOverGroupPath"
      @drag-start="(e, c, p) => handleDragStart(e, c as CriterionItem, p)"
      @drag-end="handleDragEnd"
      @drag-over="handleDragOver"
      @drop="handleDrop"
      @add-rule="(p, t) => addRule(p, t)"
      @add-group="addGroup"
      @remove-item="removeItem"
      @toggle-operator="toggleGroupOperator"
      @toggle-rule-not="toggleRuleNot"
      @update-rule-value="
        (p: number[], v: unknown) =>
          updateRuleValue(p, v as string | number | (string | number)[] | null)
      "
      @update-object-field="
        (p: number[], f: string, o: CriterionTypeValue, v: string | number) =>
          updateObjectField(p, f, o, v)
      "
      @change-rule-type="changeRuleType"
      @validation-error="handleValidationError"
    />
  </div>
</template>

<style scoped>
.recursive-query-builder {
  width: 100%;
  padding: 0;
  background: transparent;
}
</style>
